import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/de';
import { useTranslation } from 'react-i18next';
import {
  useCreateMilestone,
  useUpdateMilestone,
} from '../../../api/hooks/useProjects';
import { useProjectInitialization } from '../../../api/hooks/useProjectInitialization';
import { useNotifications } from '../../../context/NotificationContext';
import { ApiError } from '../../../api/client';
import { AppDialogTitle } from '../../../components/common/AppDialogTitle';

interface MilestoneFormData {
  name: string;
  description: string;
  deadline: Dayjs | null;
}

/**
 * Teilziele aus dem Projektauftrag (Ziele-Tab, „eines pro Zeile") als Namens-Vorschläge
 * (PHQ-83). Aufzählungszeichen/Nummern am Zeilenanfang fallen weg; bereits als
 * Meilenstein angelegte Namen werden nicht erneut vorgeschlagen.
 */
export function teilzielVorschlaege(zieleTeil: string, vorhandeneNamen: string[]): string[] {
  const vorhanden = new Set(vorhandeneNamen.map((n) => n.trim().toLowerCase()));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of zieleTeil.split(/\r?\n/)) {
    const name = line.replace(/^\s*(?:[-•*]|\d+[.)])\s*/, '').trim();
    const key = name.toLowerCase();
    if (!name || seen.has(key) || vorhanden.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

interface MilestoneFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  projectId: string;
  initialData?: Record<string, unknown>;
  /** Namen bestehender Meilensteine — werden nicht als Teilziel-Vorschlag angeboten. */
  existingNames?: string[];
  onClose: () => void;
}

const INITIAL_FORM: MilestoneFormData = {
  name: '',
  description: '',
  deadline: null,
};

export function MilestoneFormDialog({ open, mode, projectId, initialData, existingNames, onClose }: MilestoneFormDialogProps) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const createMutation = useCreateMilestone();
  const updateMutation = useUpdateMilestone();

  const { data: initData } = useProjectInitialization(projectId);
  const vorschlaege = useMemo(
    () => (mode === 'create' ? teilzielVorschlaege(initData.ziele_teil ?? '', existingNames ?? []) : []),
    [mode, initData.ziele_teil, existingNames],
  );

  const [form, setForm] = useState<MilestoneFormData>(INITIAL_FORM);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initialData) {
      setForm({
        name: (initialData.name as string) ?? '',
        description: (initialData.description as string) ?? '',
        deadline: initialData.deadline ? dayjs(initialData.deadline as string) : null,
      });
    } else {
      setForm(INITIAL_FORM);
    }
  }, [open, mode, initialData]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      notify(tc('errors.requiredFields', 'Pflichtfelder ausfüllen'), 'warning');
      return;
    }

    // Rang/Status/Verantwortlich sind nicht mehr im Dialog (PHQ-83): beim Anlegen gelten die
    // Standardwerte, beim Bearbeiten bleiben die bestehenden Werte unangetastet (Patch ohne diese Felder).
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      description: form.description || undefined,
      deadline: form.deadline?.format('YYYY-MM-DD') ?? null,
      ...(mode === 'edit' ? {} : { rank: 'NORMAL', state: 'CREATED' }),
    };

    try {
      if (mode === 'edit' && initialData?.id) {
        await updateMutation.mutateAsync({
          projectId,
          milestoneId: initialData.id as string,
          data: payload,
        });
      } else {
        await createMutation.mutateAsync({ projectId, data: payload });
      }
      notify(tc('status.saved', 'Gespeichert'), 'success');
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : tc('errors.generic', 'Fehler');
      notify(msg, 'error');
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const updateField = <K extends keyof MilestoneFormData>(key: K, value: MilestoneFormData[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
    <Dialog open={open} onClose={() => !isSaving && onClose()} maxWidth="sm" fullWidth>
      <AppDialogTitle>
        {mode === 'edit'
          ? t('milestones.editTitle', 'Meilenstein bearbeiten')
          : t('milestones.createTitle', 'Neuer Meilenstein')}
      </AppDialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* Name frei eingebbar; im Anlegen-Modus die Teilziele des Projektauftrags als Vorschlag. */}
          <Autocomplete
            freeSolo
            options={vorschlaege}
            inputValue={form.name}
            onInputChange={(_, v) => updateField('name', v)}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                required
                label={t('fields.name', 'Name')}
                helperText={vorschlaege.length > 0
                  ? t('milestones.teilzielHint', 'Vorschläge aus den Teilzielen des Projektauftrags')
                  : undefined}
              />
            )}
          />

          {/* Beim Bearbeiten nur Titel und Datum (PHQ-101). */}
          {mode === 'create' && (
            <TextField
              fullWidth
              multiline
              rows={2}
              label={t('fields.description', 'Beschreibung')}
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
            />
          )}

          <DatePicker
            label={t('fields.deadline', 'Deadline')}
            value={form.deadline}
            onChange={(v) => updateField('deadline', v)}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          {tc('actions.cancel', 'Abbrechen')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || !form.name.trim()}
          startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
        >
          {tc('actions.save', 'Speichern')}
        </Button>
      </DialogActions>
    </Dialog>
    </LocalizationProvider>
  );
}
